import { useEffect, useState } from "react";
import type { JSX } from "react";
import type { PendingToolCall } from "../../modules/episodes/types";
import type { AllowlistScope, AllowlistState } from "../../runtime/contracts";
import { Icon } from "../ui/Icon";

export function AllowlistDropdown({
  isAllowlisted,
  onToggle,
}: {
  readonly isAllowlisted: AllowlistState;
  readonly onToggle: (scope?: AllowlistScope) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);

  if (isAllowlisted !== false) {
    return (
      <button
        className="flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1.5 text-[12px] text-emerald-400"
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onToggle();
        }}
      >
        <Icon name="circle-check" size={12} />
        {isAllowlisted === "dialog" ? "Allowed in this dialog" : "Allowed always"}
      </button>
    );
  }

  return (
    <div className="relative">
      <button
        className="flex items-center gap-1 rounded-md border border-agent-border px-2.5 py-1.5 text-[12px] text-agent-text-muted"
        type="button"
        onClick={() => { setOpen(!open); }}
      >
        <Icon name="chevron-down" size={12} />
        Allowlist
      </button>
      {open && (
        <div className="absolute bottom-full left-0 mb-1 min-w-[160px] rounded-md border border-edge bg-surface-secondary shadow-lg z-50">
          <button
            type="button"
            className="w-full px-3 py-2 text-left text-[12px] text-agent-text-muted hover:bg-surface-hover rounded-t-md"
            onClick={(event) => {
              event.stopPropagation();
              setOpen(false);
              onToggle("dialog");
            }}
          >
            Allow in this dialog
          </button>
          <button
            type="button"
            className="w-full px-3 py-2 text-left text-[12px] text-agent-text-muted hover:bg-surface-hover rounded-b-md"
            onClick={(event) => {
              event.stopPropagation();
              setOpen(false);
              onToggle("always");
            }}
          >
            Allow always
          </button>
        </div>
      )}
    </div>
  );
}

export interface ToolApprovalCardProps {
  readonly toolCall: PendingToolCall;
  readonly onApprove: () => Promise<void> | void;
  readonly onDeny: () => Promise<void> | void;
}

export function ToolApprovalCard({
  toolCall,
  onApprove,
  onDeny,
}: ToolApprovalCardProps): JSX.Element {
  const args = toolCall.args as Record<string, unknown>;
  const externalStatus = toolCall.status;
  const [approving, setApproving] = useState(false);
  const [denying, setDenying] = useState(false);
  const [approved, setApproved] = useState(externalStatus === "approved");
  const [denied, setDenied] = useState(externalStatus === "denied");
  const [approveError, setApproveError] = useState<string | null>(null);

  useEffect(() => {
    setApproved(externalStatus === "approved");
    setDenied(externalStatus === "denied");
  }, [externalStatus]);

  const isBusy = approving || denying;
  const isDone = approved || externalStatus === "approved";
  const isDenied = denied || externalStatus === "denied";
  // @tested-by: tst_fe_tool_approval_001, tst_fe_tool_approval_002,
  //             tst_fe_tool_approval_003, tst_fe_tool_approval_004,
  //             tst_fe_tool_approval_005
  const isPending = !isDone && !isDenied;

  const doApprove = async (): Promise<void> => {
    setApproving(true);
    setApproveError(null);
    try {
      await onApprove();
      setApproved(true);
    } catch (err) {
      setApproveError(err instanceof Error ? err.message : "Approval failed");
    } finally {
      setApproving(false);
    }
  };

  const doDeny = async (): Promise<void> => {
    setDenying(true);
    setApproveError(null);
    try {
      await onDeny();
      setDenied(true);
    } catch (err) {
      setApproveError(err instanceof Error ? err.message : "Deny failed");
    } finally {
      setDenying(false);
    }
  };

  return (
    <div className={`rounded-xl border border-edge bg-surface-tertiary px-3.5 py-3 ${isBusy ? "opacity-70 pointer-events-none" : ""}`}>
      <div className="mb-2 flex items-center gap-2">
        <span className="text-[#FF8400]">
          <Icon name="shield-alert" size={14} />
        </span>
        <span className="text-[13px] font-semibold text-agent-text-heading">
          Agent wants to: {toolCall.name.replace(/_/g, " ")}
        </span>
      </div>

      {"text" in args && Boolean(args.text) ? (
        <div className="mb-2.5 rounded-lg bg-surface border border-edge px-3 py-2">
          <p className="text-[12px] text-agent-text-dim mb-0.5">Message:</p>
          <p className="text-[13px] text-agent-text whitespace-pre-wrap">
            {String(args.text)}
          </p>
        </div>
      ) : null}

      {"chat_id" in args && Boolean(args.chat_id) ? (
        <p className="mb-2.5 text-[12px] text-agent-text-dim">
          Chat ID:{" "}
          <span className="text-agent-text-muted">{String(args.chat_id)}</span>
        </p>
      ) : null}

      {approveError && (
        <p className="mb-2 text-[11px] text-red-400">{approveError}</p>
      )}

      {isPending ? (
        <div className="flex items-center gap-2">
          <button
            className="rounded-md border border-agent-border px-4 py-1.5 text-[12px] font-medium text-agent-text-muted hover:bg-white/5"
            type="button"
            onClick={() => { void doDeny(); }}
          >
            {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
            {denying ? "Denying..." : "Deny"}
          </button>
          <button
            className="flex items-center gap-1.5 rounded-md bg-emerald-500 px-4 py-1.5 text-[12px] font-medium text-white hover:bg-emerald-400"
            type="button"
            onClick={() => void doApprove()}
          >
            <Icon name="circle-check" size={13} />
            {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
            {approving ? "Approving..." : "Approve"}
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-[12px]">
          <span
            className={
              isDone ? "text-emerald-400" : "text-red-400"
            }
          >
            <Icon
              name={isDone ? "circle-check" : "close"}
              size={13}
            />
          </span>
          <span className="text-agent-text-muted">
            {isDone ? "Approved" : "Denied"}
          </span>
        </div>
      )}
    </div>
  );
}
